// Edition form for team card

import * as Yup from 'yup';
import { teamUpdate, teamCreate } from '../utils/firebase';
import Input from './Input';
import InputLocation from './InputLocation';
import Select from './Select';
import CardEditTemplate from './CardEditTemplate';
import GuidedTour from './GuidedTour';
import Fieldset from './Fieldset';
import RadioButton from './RadioButton';
import { Sports, Genders, CutOffDates } from '../utils/enums';
import { ageLabels, groupByLabel, AgeGroupTypes } from '../utils/agegroups';
import { switchGender } from '../utils/misc';
import { useFirebase } from './FirebaseContextProvider';
import placeholderTeam from '../assets/images/placeholder-team.png';
import './TeamCardEdit.scss';

const steps = [
  {
    target: '.team-card-edit',
    title: 'Team information',
    content: 'Start by filling in the general team information',
  },
];

export default function TeamCardEdit({ team }) {
  const { user, userIsCoach } = useFirebase();
  const { id, name, location, age, gender, sport, imgUrl, imgThumbUrl, cutOffDate } = team;

  const initialValues = {
    name: name || '',
    location: location || '',
    age: age || null,
    gender: gender || null,
    sport: sport || null,
    cutOffDate: cutOffDate || CutOffDates.AUG_1,
  };

  const validationSchema = new Yup.ObjectSchema({
    name: Yup.string().max(100, 'Too long').required('Required'),
    location: Yup.string().max(100, 'Too long').required('Required'),
    age: Yup.string().required('Required'),
    gender: Yup.string().required('Required'),
    sport: Yup.string().required('Required'),
  });

  const wrapArgsNormalize = (obj) => {
    const { location } = obj;
    const tmpObj = { ...obj };
    if (location) tmpObj.location = location.trim().replace(/\s{2,}/g, ' ');
    return tmpObj;
  };

  return (
    <GuidedTour steps={userIsCoach ? steps : []} tourKey='team-card-edit-v1'>
      <CardEditTemplate
        className='team-card-edit'
        id={id}
        imgUrl={imgUrl}
        imgThumbUrl={imgThumbUrl}
        placeholder={placeholderTeam}
        formConfig={{
          initialValues,
          validationSchema,
        }}
        create={(args) => teamCreate(wrapArgsNormalize(args))}
        update={(args) => teamUpdate(wrapArgsNormalize(args))}
        afterCreateNavPath={
          userIsCoach ? `/coaches/${user.id}#teams` : '/admin#teams'
        }
      >
        {(formik) => (
          <>
            <Input
              name='name'
              placeholder='Team name'
              {...formik.getFieldProps('name')}
              error={
                formik.touched.name && formik.errors.name
                  ? formik.errors.name
                  : null
              }
            />
            <div className='form-body'>
              <div className='form-main'>
                <InputLocation
                  level='city'
                  value={formik.values.location}
                  onChange={(val) => formik.setFieldValue('location', val)}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.location && formik.errors.location
                      ? formik.errors.location
                      : null
                  }
                />
                <div className='select-wrap'>
                  <Select
                    options={Object.values(Sports)}
                    value={formik.values.sport}
                    placeholder='Sport'
                    onChange={async (val) => {
                      await formik.setFieldValue('sport', val);
                      await formik.setTouched({ ...formik.touched, sport: true });
                    }}
                    error={
                      formik.touched.sport && formik.errors.sport
                        ? formik.errors.sport
                        : null
                    }
                  />
                  <Select
                    options={Object.values(Genders).map((gender) =>
                      switchGender(gender)
                    )}
                    value={switchGender(formik.values.gender)}
                    placeholder='Gender'
                    onChange={async (val) => {
                      await formik.setFieldValue('gender', switchGender(val));
                      await formik.setTouched({ ...formik.touched, gender: true });
                    }}
                    error={
                      formik.touched.gender && formik.errors.gender
                        ? formik.errors.gender
                        : null
                    }
                  />
                  <Select
                    options={ageLabels}
                    value={formik.values.age}
                    placeholder='Age'
                    onChange={async (val) => {
                      await formik.setFieldValue('age', val);
                      await formik.setTouched({ ...formik.touched, age: true });
                    }}
                    error={
                      formik.touched.age && formik.errors.age
                        ? formik.errors.age
                        : null
                    }
                  />
                </div>
              </div>
              {groupByLabel(formik.values.age)?.type === AgeGroupTypes.U && (
                <Fieldset label='Cut-off date' className='cut-off-date'>
                  <RadioButton
                    label={CutOffDates.AUG_1}
                    name='cutOffDate'
                    value={CutOffDates.AUG_1}
                    selectedValue={formik.values.cutOffDate}
                    onChange={(val) => formik.setFieldValue('cutOffDate', val)}
                  />
                  <RadioButton
                    label={CutOffDates.JAN_1}
                    name='cutOffDate'
                    value={CutOffDates.JAN_1}
                    selectedValue={formik.values.cutOffDate}
                    onChange={(val) => formik.setFieldValue('cutOffDate', val)}
                  />
                </Fieldset>
              )}
            </div>
          </>
        )}
      </CardEditTemplate>
    </GuidedTour>
  );
}
